// ============================================
// Agent Brand Service
// Fix 2: Loads brand identity for AI agents from
// Firestore brand_config. Cached per session.
// Agents call getAgentBrand() at initialization
// instead of hardcoding brand strings in prompts.
// ============================================

import { getBrand } from './docopsService';
import type { BrandConfig } from '../../types/docops';
import { DEFAULT_BRAND_CONFIG } from '../../data/docops/defaultBrandConfig';

// ── Agent boundary constraint types ──

export type AgentBoundary =
  | 'no_diagnosis'
  | 'no_treatment_advice'
  | 'scope_to_analysis'
  | 'no_clinical_claims'
  | 'no_personal_details'
  | 'no_prescribing'
  | 'scope_to_intake'
  | 'formulary_only'
  | 'no_off_label'
  | 'safety_gate_required'
  | 'scope_to_interpretation'
  | 'no_fabricated_evidence'
  | 'no_diagnostic_language';

export type AgentId = 'captain' | 'nurse' | 'protocol_builder' | 'report_generator';

export interface AgentPersonality {
  id: AgentId;
  role: string;
  description: string;
  boundaries: AgentBoundary[];
}

// ── Agent personalities with boundaries ──
// Each agent has a defined set of boundary constraints that map
// to specific prompt-level restrictions. See docs/hlx-c15-agent-boundaries.md
// for the full mapping of each boundary to its prompt constraint.

export const AGENT_PERSONALITIES: Record<AgentId, AgentPersonality> = {
  captain: {
    id: 'captain',
    role: 'Lab Analyst (Captain)',
    description: 'Clinical authority agent — extracts and analyzes biomarker data from lab reports. Structured output only.',
    boundaries: [
      'no_diagnosis',
      'no_treatment_advice',
      'scope_to_analysis',
      'no_fabricated_evidence',
      'no_diagnostic_language',
    ],
  },
  nurse: {
    id: 'nurse',
    role: 'Intake Nurse (Flight Attendant)',
    description: 'Patient-facing conversational agent — collects concerns, goals, medications, allergies. Emotional translator.',
    boundaries: [
      'no_clinical_claims',
      'no_personal_details',
      'no_prescribing',
      'scope_to_intake',
      'no_diagnostic_language',
    ],
  },
  protocol_builder: {
    id: 'protocol_builder',
    role: 'Protocol Builder',
    description: 'Maps biomarker gaps to approved formulary products. Every recommendation must pass safety checks.',
    boundaries: [
      'formulary_only',
      'no_off_label',
      'safety_gate_required',
      'no_diagnosis',
      'no_fabricated_evidence',
    ],
  },
  report_generator: {
    id: 'report_generator',
    role: 'Report Generator',
    description: 'Generates narrative interpretation of lab results against functional and longevity ranges.',
    boundaries: [
      'no_diagnosis',
      'no_treatment_advice',
      'scope_to_interpretation',
      'no_personal_details',
      'no_diagnostic_language',
    ],
  },
};

/** Look up boundaries for a specific agent. */
export function getAgentBoundaries(agentId: AgentId): AgentBoundary[] {
  return AGENT_PERSONALITIES[agentId].boundaries;
}

// ── Agent identity shape (what agents actually need) ──

export interface AgentBrandIdentity {
  platformName: string;
  platformNameFormal: string;
  nurseRole: string;
  analystRole: string;
  protocolBuilderRole: string;
  reportEngineRole: string;
  kFrameworkOwner: string;
  clinicalPhilosophy: string;
  disclaimerShort: string;
  medicalDirectorName: string;
  brandEntity: string;
  marketVertical: string;
  productType: string;
}

// ── Defaults (from DEFAULT_BRAND_CONFIG) ──

const DEFAULTS: AgentBrandIdentity = {
  platformName: DEFAULT_BRAND_CONFIG.config.agentIdentity?.platformName ?? 'Hemologix',
  platformNameFormal: DEFAULT_BRAND_CONFIG.config.agentIdentity?.platformNameFormal ?? 'HemoLogix',
  nurseRole: DEFAULT_BRAND_CONFIG.config.agentIdentity?.nurseRole ?? 'Hemologix Intake Nurse',
  analystRole: DEFAULT_BRAND_CONFIG.config.agentIdentity?.analystRole ?? 'HemoLogix Lab Analyst (Captain)',
  protocolBuilderRole: DEFAULT_BRAND_CONFIG.config.agentIdentity?.protocolBuilderRole ?? 'HemoLogix Protocol Builder',
  reportEngineRole: DEFAULT_BRAND_CONFIG.config.agentIdentity?.reportEngineRole ?? 'Hemologix Report Engine',
  kFrameworkOwner: DEFAULT_BRAND_CONFIG.config.agentIdentity?.kFrameworkOwner ?? 'Stan Van Meter',
  clinicalPhilosophy: DEFAULT_BRAND_CONFIG.config.agentIdentity?.clinicalPhilosophy ?? 'evidence-based reasoning, intellectual honesty, and a commitment to patient autonomy',
  disclaimerShort: DEFAULT_BRAND_CONFIG.config.agentIdentity?.disclaimerShort ?? 'I provide wellness optimization analysis, not medical advice. Always consult your healthcare provider.',
  medicalDirectorName: DEFAULT_BRAND_CONFIG.config.medicalDirectorName || 'Dr. Christopher Calapai, D.O.',
  brandEntity: DEFAULT_BRAND_CONFIG.config.brandEntity,
  marketVertical: DEFAULT_BRAND_CONFIG.config.marketVertical,
  productType: DEFAULT_BRAND_CONFIG.config.productType,
};

// ── Cache ──

let _cached: AgentBrandIdentity | null = null;
let _loaded = false;

function extractIdentity(config: BrandConfig): AgentBrandIdentity {
  const ai = config.agentIdentity;
  return {
    platformName: ai?.platformName || DEFAULTS.platformName,
    platformNameFormal: ai?.platformNameFormal || DEFAULTS.platformNameFormal,
    nurseRole: ai?.nurseRole || DEFAULTS.nurseRole,
    analystRole: ai?.analystRole || DEFAULTS.analystRole,
    protocolBuilderRole: ai?.protocolBuilderRole || DEFAULTS.protocolBuilderRole,
    reportEngineRole: ai?.reportEngineRole || DEFAULTS.reportEngineRole,
    kFrameworkOwner: ai?.kFrameworkOwner || DEFAULTS.kFrameworkOwner,
    clinicalPhilosophy: ai?.clinicalPhilosophy || DEFAULTS.clinicalPhilosophy,
    disclaimerShort: ai?.disclaimerShort || DEFAULTS.disclaimerShort,
    medicalDirectorName: config.medicalDirectorName || DEFAULTS.medicalDirectorName,
    brandEntity: config.brandEntity || DEFAULTS.brandEntity,
    marketVertical: config.marketVertical || DEFAULTS.marketVertical,
    productType: config.productType || DEFAULTS.productType,
  };
}

/**
 * Load agent brand identity from Firestore (cached after first call).
 * Falls back to defaults if Firestore is unavailable.
 */
export async function getAgentBrand(brandId = 'hemologix'): Promise<AgentBrandIdentity> {
  if (_loaded && _cached) return _cached;

  try {
    const brand = await getBrand(brandId);
    if (brand) {
      _cached = extractIdentity(brand.config);
    } else {
      _cached = { ...DEFAULTS };
    }
  } catch {
    console.warn('[AgentBrand] Firestore unavailable, using defaults');
    _cached = { ...DEFAULTS };
  }

  _loaded = true;
  return _cached;
}

/** Synchronous access — returns cached value or defaults. */
export function getAgentBrandSync(): AgentBrandIdentity {
  return _cached ?? { ...DEFAULTS };
}

/** Reset cache (for testing or brand switch). */
export function resetAgentBrandCache(): void {
  _cached = null;
  _loaded = false;
}
